'use client'

/**
 * KpiCard — the ONLY allowed KPI display surface (Design Language Codex §5, §12).
 *
 * CANONICAL RULES:
 * - No sentences in KPI cards. Support line = one short phrase; meta = benchmark context.
 * - Benchmark context (e.g. "Median competitor: 14%") goes in `meta`, not `support`.
 * - No custom KPI layouts outside this component — compound parts are for the 10% edge case.
 * - No more than 5 KPIs per strip.
 *
 * `KpiCardGroup` renders the joined strip: a single border wrapping all cells,
 * gap-px + bg-border to produce 1px hairline dividers between cells.
 * Use `KpiCardGroup` instead of a bare grid whenever cells share a visual border.
 *
 * Sparkline is the only Recharts (client-side) part; the rest is pure markup.
 * The whole file is 'use client' because Recharts always needs a client boundary.
 * All existing consumers are already client components so this is no regression.
 *
 * The selectable-tile variant (`onSelect`) renders as `<Button
 * variant="unstyled" size="auto">` (ButtonPrimitive-migration, 2026-08-30) —
 * the tile's own flex-col layout, padding and background are 100% its own via
 * `wrapperClassName`; the atom supplies only real `<button>` semantics and
 * the shared focus-ring. This satisfies the shared/ lint rule (raw <button>
 * is fenced there) without a sized rung fighting the tile's own geometry.
 */

import { AreaChart, Area, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendDelta, type TrendDeltaVerdict } from '@/components/shared/trend-delta'
import { CHART_TOOLTIP_STYLE } from '@/components/shared/chart-config'
import { IntervalLine } from '@/components/shared/interval-readout'
import { InfoPopover } from '@/components/shared/info-popover'
import {
  formatSparklineValue,
  intervalSegments,
  type IntervalSegmentsInput,
  type SparklineFormat,
} from '@/lib/format'
import { cn } from '@/lib/utils'

// ─── Sparkline tooltip label style (compact variant of CHART_TOOLTIP_LABEL_STYLE) ───
// Derives from the canonical style — marginBottom is tighter (2px vs 6px) for
// the compact sparkline hover. Spread CHART_TOOLTIP_STYLE for the container.
const SPARKLINE_TOOLTIP_LABEL_STYLE = {
  color: 'var(--muted-foreground)',
  marginBottom: 2,
  fontWeight: 500,
} as const

// ─── Sparkline value formats ──────────────────────────────────────────────────
// Serializable token instead of a function so server components can pass it
// across the RSC boundary (functions are not serializable).

export type KpiSparklineFormat = SparklineFormat

// ─── Value tone (the one sanctioned exception to "never colorize the value") ─
// `growth-engine-r3.3-phase3` — a KPI whose value IS itself a bad-news count (open findings,
// orphan pages) needs the number to carry the same negative/warning read its own copy states, not
// a neutral render that then contradicts an adjacent red/amber support line. Restricted to the
// semantic trio's tokens (DES-09) — never a raw color, and never used decoratively on a card whose
// value isn't inherently the negative/warning fact.
export type KpiValueTone = 'default' | 'negative' | 'warning'

const VALUE_TONE_CLASS: Record<KpiValueTone, string> = {
  default: 'text-foreground',
  negative: 'text-negative',
  warning: 'text-warning',
}

// ─── Internal sparkline (not exported) ───────────────────────────────────────

interface KpiSparklineProps {
  id: string
  data: { date: string; v: number }[]
  format?: KpiSparklineFormat
}

function KpiSparkline({ id, data, format }: KpiSparklineProps) {
  const values = data.map((d) => d.v)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const isFlat = max === min
  const pad = (max - min || 1) * 0.4

  return (
    // Decorative — the card's primary value is the accessible number
    <div className="h-8 w-20 shrink-0" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
          {!isFlat && (
            <defs>
              <linearGradient id={`kpi-spark-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.22} />
                <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
              </linearGradient>
            </defs>
          )}
          <YAxis hide domain={[min - pad, max + pad]} />
          <Tooltip
            cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={SPARKLINE_TOOLTIP_LABEL_STYLE}
            formatter={(value) => [
              format ? formatSparklineValue(format, Number(value)) : String(value),
              '',
            ]}
            separator=""
            wrapperStyle={{ zIndex: 10 }}
          />
          {/* flat series → solid muted line, no gradient fill */}
          <Area
            type="monotone"
            dataKey="v"
            stroke={isFlat ? 'var(--bar-neutral)' : 'var(--chart-1)'}
            strokeWidth={1.5}
            fill={isFlat ? 'transparent' : `url(#kpi-spark-${id})`}
            dot={false}
            activeDot={isFlat ? false : { r: 3, stroke: 'var(--card)', strokeWidth: 1.5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─── Compound parts (for the 10% edge case) ──────────────────────────────────

/**
 * KpiCard.Root — the cell container.
 * `className` is accepted ONLY for grid placement (e.g. `col-span-2`).
 * Never use it to override spacing, background, or padding.
 */
function Root({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-3 bg-card p-5', className)}>{children}</div>
}

/** KpiCard.Label — the u-label eyebrow with fixed h-4 (truncated) */
function Label({ children }: { children: React.ReactNode }) {
  return <p className="u-label min-w-0 truncate whitespace-nowrap leading-4">{children}</p>
}

/** KpiCard.Value — the 30px tabular KPI number */
function Value({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-value font-semibold leading-none tracking-tight tabular-nums text-foreground">
      {children}
    </span>
  )
}

/** KpiCard.Delta — wraps TrendDelta for use inside the label row */
function Delta(props: React.ComponentProps<typeof TrendDelta>) {
  return <TrendDelta {...props} />
}

/** KpiCard.Chart — the sparkline slot (h-8 w-20 container) */
function Chart({ children }: { children: React.ReactNode }) {
  return <div className="h-8 w-20 shrink-0">{children}</div>
}

/** KpiCard.Support — the 14px foreground/80 line below the value */
function Support({ children }: { children: React.ReactNode }) {
  return <p className="text-body text-foreground/80">{children}</p>
}

/** KpiCard.Meta — 12px muted benchmark context line */
function Meta({ children }: { children: React.ReactNode }) {
  return <p className="text-caption text-muted-foreground">{children}</p>
}

// ─── Façade props ─────────────────────────────────────────────────────────────

export interface KpiCardDelta {
  /** Positive = up, negative = down, 0 = flat, null = no data. */
  value: number | null
  /** How to format the magnitude. Defaults to 'pp'. */
  format?: 'pp' | 'pct' | 'num' | 'pos' | 'count'
  /** Invert goodness semantics for lower-is-better metrics (position, cost). */
  invert?: boolean
}

/**
 * Measurement-confidence verdict — `real`/`noise`/`insufficient_data` from the
 * confidence pipeline (`docs/tasks/measurement-confidence/{spec,design-spec}.md`
 * §7.3/§B), plus `unverified` for a raw observed move with no statistical test run
 * on it (Part 2 — Share of Voice, Avg Position). Passed as `{ verdict }` instead of
 * the legacy `{ value, format, invert }` shape; `KpiCard` forwards it straight to
 * `TrendDelta`'s own `verdict` prop.
 */
export type KpiCardVerdictDelta = { verdict: TrendDeltaVerdict }

export type KpiCardDeltaProp = KpiCardDelta | KpiCardVerdictDelta

export interface KpiCardProps {
  /** Eyebrow label — short, uppercase, no sentence punctuation. */
  label: string
  /**
   * Optional leading icon rendered before the label (e.g. an EngineLogo).
   * Sized to the label row (16px). Decorative — give it an aria-label if it
   * conveys meaning not already in the label text.
   */
  icon?: React.ReactNode
  /**
   * Optional info trigger rendered beside the label (e.g. an InfoPopover).
   * One per visible card header max (codex).
   */
  labelInfo?: React.ReactNode
  /**
   * Pre-formatted primary value string. Format at the call site via format.ts.
   * Example: `pct(mentionRate)`, `pos(avgPosition)`, `num(count)`.
   */
  value: string
  /**
   * Margin of error, baseline-aligned with `value` (design-spec.md §A):
   * `47% ±8pp`. Smaller/lighter/muted, rendered directly after `value` on the
   * same line — wraps beneath at a matching left edge when the card is too
   * narrow, never truncated or tooltip-only. Pre-format at the call site
   * (e.g. `marginPp(...)` in `format.ts`).
   */
  margin?: string
  /** Delta badge rendered right-aligned on the label row. */
  delta?: KpiCardDeltaProp
  /**
   * R6.3 B1 — the one interval form under a rate's value, rendered through `IntervalLine`
   * (`interval-readout.tsx`) at the head of row 3, above `support`: "11% to 26% · 24 questions ·
   * 28 days". Pass the metric's own parts (`Metric.toParts()`'s `range`/`n`/`scope`) with the unit
   * word — never a hand-built line. `n <= 0` renders no line (the value is then the no-data glyph).
   * Optional and additive: omitted, the card renders exactly as before.
   */
  interval?: IntervalSegmentsInput
  /**
   * Short support phrase below the value. One line. No sentences.
   * Example: "of AI answers mention you"
   *
   * A node (not only a string) so a phrase that depends on the viewer — a date in their own time
   * zone — can sit here (R6.16 B5); every other caller passes a string.
   */
  support?: React.ReactNode
  /**
   * R6.16 B5 (FU-203) — the full sentence behind a short `support` reason. When given, the support
   * renders as ONE truncated line followed by an info button (`InfoPopover`, click/keyboard/touch
   * reachable) holding this detail, so a long reason never wraps and a strip's cells stay
   * top-aligned. Needs `support`; omitted, the support renders exactly as before.
   */
  supportDetail?: React.ReactNode
  /** The `supportDetail` info button's accessible name. Defaults to a neutral "Details"; a caller
   * whose detail explains a missing figure says so ("Why there is no figure"). */
  supportDetailLabel?: string
  /**
   * Benchmark context — 12px muted. Lives here per codex §12.
   * Example: "Median competitor: 14%", or an `InlineLink` to the methodology
   * page (`How we calculate this →`) on any card showing a `margin`.
   */
  meta?: React.ReactNode
  /** Inline sparkline data. Rendered at h-8 w-20 to the right of the value. */
  sparkline?: {
    data: { date: string; v: number }[]
    /**
     * Tooltip value format token (serializable — safe to pass from server
     * components). 'pct' → "27%" (a 0–100 value, "<1%" under one percent), 'pos' → "#3.4".
     */
    format?: KpiSparklineFormat
  }
  /** Show skeleton loading state matching the full anatomy. */
  loading?: boolean
  /**
   * When `loading`, also render a margin-sized skeleton chip beside the value
   * skeleton — only meaningful for cards that will show `margin` once loaded.
   */
  loadingHasMargin?: boolean
  /**
   * Grid placement override only (e.g. `col-span-2`).
   * Never use to override spacing, background, or typography.
   */
  className?: string
  /**
   * UPDATED (growth-engine-r2, Visibility shared pattern layer) — makes this
   * tile clickable. Ported from the Claude Design source's `MetricStrip`
   * pattern (`docs/tasks/growth-engine-r2/design-export/shared/Patterns.jsx`
   * calls this a "selectable tile" and notes it renders as a real
   * `<button>`), where selecting a KPI tile drives which metric a chart below
   * it plots. Extending the existing atom in place (rather than a parallel
   * "MetricTile" component) per DES-50 and the standing `KpiCard` contract:
   * every prop here is optional and the unselectable render path (no
   * `onSelect`) is untouched, so this is additive for the ~29 existing
   * callers that never pass it.
   */
  onSelect?: () => void
  /** Whether this tile is the active selection. Only meaningful with `onSelect`. */
  selected?: boolean
  /**
   * Semantic tone for the primary value number. `'negative'`/`'warning'` route through
   * `--negative`/`--warning` (DES-09 — never a raw color). This is the one sanctioned exception to
   * the "never colorize the value" rule below: reserve it for a value that IS itself the bad-news
   * count (open findings, orphan pages), never for decoration. Defaults to `'default'`
   * (`text-foreground`, unchanged for every existing caller).
   */
  valueTone?: KpiValueTone
  /**
   * Let the label wrap instead of truncating (overview-360: "AI assistant visits" in the Overview's
   * five-cell Google strip read "AI ASSISTANT…" at desktop width). The delta stays on the label's
   * first line, and the label row reserves two lines from `lg`, so in a `KpiCardGroup` row every
   * value stays level with a neighbour whose label wrapped — and with one whose support/meta block
   * is taller. Opt-in: omitted, the tile renders exactly as before.
   */
  wrapLabel?: boolean
}

// ─── Façade component ─────────────────────────────────────────────────────────

/**
 * Row 1 — label (h-4, truncate; or wrapping with `wrapLabel`) + optional icon and info trigger +
 * delta right-aligned. With `wrapLabel` the delta is held to the label's first line (h-4) rather
 * than the row's middle.
 */
function KpiLabelRow({
  label,
  icon,
  labelInfo,
  delta,
  wrapLabel,
}: Pick<KpiCardProps, 'label' | 'icon' | 'labelInfo' | 'delta'> & {
  wrapLabel: boolean
}): React.ReactElement {
  const deltaNode =
    delta === undefined ? null : 'verdict' in delta ? (
      <TrendDelta verdict={delta.verdict} />
    ) : (
      <TrendDelta value={delta.value} format={delta.format ?? 'pp'} invert={delta.invert} />
    )

  return (
    <div
      className={cn(
        'flex',
        // Wrapping: two label lines are reserved at `lg` (where the five-cell strip sits), so a
        // one-line label and a wrapped one put their values at the same y.
        wrapLabel ? 'min-h-4 items-start lg:min-h-8' : 'h-4 items-center',
        'justify-between gap-2'
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {icon != null && (
          <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
        )}
        <p
          className={cn(
            'u-label min-w-0',
            wrapLabel ? 'text-pretty' : 'truncate whitespace-nowrap',
            'leading-4'
          )}
        >
          {label}
        </p>
        {labelInfo != null && <span className="flex shrink-0 items-center">{labelInfo}</span>}
      </span>
      {deltaNode != null && wrapLabel ? (
        <span className="flex h-4 shrink-0 items-center">{deltaNode}</span>
      ) : (
        deltaNode
      )}
    </div>
  )
}

/** The support line: one plain paragraph, or — with a detail — one truncated line followed by the
 * detail's info button, named neutrally ("Details") unless the caller says why it is there. */
function KpiSupportLine({
  support,
  detail,
  detailLabel = 'Details',
}: {
  support: React.ReactNode
  detail?: React.ReactNode
  detailLabel?: string
}): React.ReactElement {
  if (detail == null) return <p className="text-body text-foreground/80">{support}</p>
  return (
    <div className="flex min-w-0 items-center gap-1 text-body text-foreground/80">
      <span className="truncate">{support}</span>
      <span className="flex shrink-0 items-center">
        <InfoPopover triggerLabel={detailLabel}>{detail}</InfoPopover>
      </span>
    </div>
  )
}

function KpiCardFacade({
  label,
  icon,
  labelInfo,
  value,
  margin,
  delta,
  interval,
  support,
  supportDetail,
  supportDetailLabel,
  meta,
  sparkline,
  loading,
  loadingHasMargin,
  className,
  onSelect,
  selected,
  valueTone = 'default',
  wrapLabel = false,
}: KpiCardProps): React.ReactElement {
  if (loading) {
    return (
      <div className={cn('flex flex-col gap-3 bg-card p-5', className)}>
        {/* Row 1: label + delta skeleton */}
        <div className="flex h-4 items-center justify-between gap-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-10" />
        </div>
        {/* Row 2: value (+ margin) + sparkline skeleton */}
        <div className="flex items-end justify-between gap-3">
          <div className="flex items-baseline gap-1.5">
            <Skeleton className="h-8 w-16" />
            {loadingHasMargin && <Skeleton className="h-4 w-10" />}
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
        {/* Row 3: meta skeleton — one line, matching the common loaded state */}
        <Skeleton className="h-3 w-32" />
      </div>
    )
  }

  // No sample → `IntervalLine` renders nothing, so row 3 must not open for it alone.
  const hasInterval = interval != null && intervalSegments(interval).length > 0

  const wrapperClassName = cn(
    // `items-center`/`justify-center` are Button's own base defaults (it
    // assumes a single-line control); this tile stacks three rows, so
    // `items-stretch` spreads them full width and `justify-start` keeps them
    // top-aligned — without it a selectable tile beside a taller sibling in
    // `KpiCardGroup` centres its rows vertically and its value sits lower than
    // the sibling's. `cursor-pointer` and the focus ring are Button's own base
    // treatment now (ButtonPrimitive-migration) — no longer repeated here.
    'flex w-full flex-col items-stretch justify-start gap-3 bg-card p-5 text-left',
    onSelect &&
      cn(
        'duration-[var(--duration-base)] hover:bg-muted/50',
        // Load-bearing: `KpiCardGroup` is `overflow-hidden` with no padding
        // and a selectable tile is a flush grid child, so Button's outward
        // `focus-ring` gets clipped to nothing without this.
        'focus-visible:ring-inset',
        selected && 'bg-muted/70'
      ),
    className
  )

  const cardBody = (
    <>
      <KpiLabelRow
        label={label}
        icon={icon}
        labelInfo={labelInfo}
        delta={delta}
        wrapLabel={wrapLabel}
      />

      {/* Row 2 — value (+ margin, baseline-aligned) + sparkline. The margin is
          real DOM text next to the value, never a tooltip (measurement-confidence
          spec.md §11 criterion 10) — and wraps beneath at a matching left edge via
          `flex-wrap` when the card is too narrow for both on one line. It follows the
          label row directly (never pushed to the tile's bottom), so a neighbour's taller
          support/meta block cannot move it: with `wrapLabel` the label row reserves the
          wrapped label's lines, which keeps every value level. */}
      <div className="flex items-end justify-between gap-3">
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <span
            className={cn(
              'text-value font-semibold leading-none tracking-tight tabular-nums',
              VALUE_TONE_CLASS[valueTone]
            )}
          >
            {value}
          </span>
          {margin && (
            <span className="text-body-sm font-medium tabular-nums text-muted-foreground">
              {margin}
            </span>
          )}
        </span>
        {sparkline && (
          <KpiSparkline
            id={label.replace(/\s+/g, '-').toLowerCase()}
            data={sparkline.data}
            format={sparkline.format}
          />
        )}
      </div>

      {/* Row 3 — interval line + support + meta (only rendered when at least one is present) */}
      {(hasInterval || support != null || meta != null) && (
        <div className="space-y-0.5">
          {hasInterval && interval && <IntervalLine {...interval} />}
          {support && (
            <KpiSupportLine
              support={support}
              detail={supportDetail}
              detailLabel={supportDetailLabel}
            />
          )}
          {meta && <p className="text-caption text-muted-foreground">{meta}</p>}
        </div>
      )}
    </>
  )

  if (onSelect) {
    return (
      <Button
        type="button"
        variant="unstyled"
        size="auto"
        onClick={onSelect}
        aria-pressed={selected}
        className={wrapperClassName}
      >
        {cardBody}
      </Button>
    )
  }

  return <div className={wrapperClassName}>{cardBody}</div>
}

// ─── KpiCardGroup — joined strip ─────────────────────────────────────────────

export type ColCount = 2 | 3 | 4 | 5

const COL_CLASS: Record<ColCount, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
  5: 'sm:grid-cols-2 lg:grid-cols-5',
}

interface KpiCardGroupProps {
  /**
   * Number of columns at the widest breakpoint.
   * 4 is the standard KPI strip; 2/3/5 for custom strips.
   * Always starts at 1 column on mobile.
   */
  cols?: ColCount
  children: React.ReactNode
  className?: string
}

/**
 * KpiCardGroup — the joined KPI strip.
 *
 * Renders children as a single bordered container where gap-px + bg-border
 * produce 1px hairline dividers between cells. Each child should be a `KpiCard`
 * or `KpiCard.Root`.
 *
 * @example
 * <KpiCardGroup cols={4}>
 *   <KpiCard label="Presence" value="18%" delta={{ value: 0, format: 'pp' }} support="…" meta="…" sparkline={…} />
 *   <KpiCard label="Share of voice" value="5.2%" delta={{ value: 0.4, format: 'pp' }} support="…" meta="…" />
 *   <KpiCard label="Citation share" value="0%" delta={{ value: 0, format: 'pp' }} support="…" meta="…" />
 *   <KpiCard label="Avg. position" value="3.4" delta={{ value: 0.5, format: 'pp', invert: true }} support="…" meta="…" />
 * </KpiCardGroup>
 */
export function KpiCardGroup({ cols = 4, children, className }: KpiCardGroupProps) {
  return (
    <div
      className={cn(
        // Edge matches <Card>: hairline ring (not a border) on the canonical --border.
        // bg-border + gap-px paints the 1px dividers between cells.
        'grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-border ring-1 ring-border',
        COL_CLASS[cols],
        className
      )}
    >
      {children}
    </div>
  )
}

// ─── Attach compound parts + export ──────────────────────────────────────────

export const KpiCard = Object.assign(KpiCardFacade, {
  Root,
  Label,
  Value,
  Delta,
  Chart,
  Support,
  Meta,
})
